import {FontAwesomeIcon as Icon} from "@fortawesome/react-fontawesome"
import {Auction, AuctionBid, AuctionRound, BiddingStep, Subject, Tireset, Vehicle} from "domain-models"
import {currency, hasFlag, make, model} from 'filters'
import _ from 'lodash'
import {Errors, FormType} from "mixins/FormTypes"
import {Http} from "mixins/http"
import moment from 'moment'
import React from "react"
import {fetchIndicators, setAuctions, showAuction} from "redux/actions"
import {store} from 'store'
import AlertDanger from "../../components/Alerts/AlertDanger"
import AlertWarning from "../../components/Alerts/AlertWarning"
import Countdown from "../../components/Countdown"
import Modal from '../../components/Modal'
import Form from '../../mixins/form'
import {AppState} from "../../redux/reducers/AppReducer"
import {AuctionsState} from "../../redux/reducers/AuctionReducer"
import {connect} from "react-redux"
import {GlobalState} from "../../state"
import CurrencyInput from "../../components/CurrencyInput"
import Trans from '../../components/Trans'

type BidModalProps = {
  auction: Auction
  hideModal: () => void
  app: AppState
  auctionsState: AuctionsState
  bid: number
}

type BidModalState = {
  form: FormType
  overbid: boolean
  bid: number
  steps: Array<BiddingStep>
  subject: Vehicle | Tireset
  correctionPending: boolean
}

type AuctionBidResponse = {
  success: boolean
  hotBid: AuctionRound | null
  Bid: AuctionBid
  errors: Errors
  bid: number
  isStillOutbid?: boolean
}

class BidModal extends React.Component<BidModalProps, BidModalState> {
  private app = this.props.app
  private auctions = this.props.auctionsState
  private form: FormType = new Form({bid: null, total: false})

  constructor(props: BidModalProps) {
    super(props)

    const firstSubject = _.first(props.auction.subjects) as Subject

    let subjectData
    subjectData = firstSubject.vehicle || firstSubject.tireset || firstSubject.leading || firstSubject.leasing

    this.state = {
      form: this.form,
      overbid: false,
      bid: this.props.bid,
      steps: [],
      subject: subjectData,
      correctionPending: false
    }
  }

  componentDidMount() {
    this.getBiddingSteps()
  }

  getBiddingSteps = () => {
    let steps = _.get(this.app, 'settings.biddingSteps', [])

    if (this.props.auction.round.Type !== 1) {
      return
    }

    let price = this.props.auction.highestBid || 0

    if (this.props.auction.round.PriceCall) {
      price = this.props.auction.round.PriceCall
    }

    steps = _.filter(steps, step => {
      return price >= step.from && price <= step.to
    })

    this.setState({steps: _.slice(steps, 0, 3)})
  }

  bid = async () => {
    this.form.bid = this.state.bid
    await this.setState({
      form: this.form,
      overbid: false,
    })
    const res = await Http.post<AuctionBidResponse>('/auctions/' + this.props.auction.Id + '/bid', {
      state: this,
      form: this.state.form
    })
    await this.setState({form: this.form, correctionPending: false})

    await fetchIndicators()

    if (res && res.success && res.hotBid) {
      this.props.auction.round.EndsOn = res.hotBid.EndsOn
    }

    if (res && !res.success && res.Bid && this.props.auction.round.Type === 1) {
      this.props.auction.highestBid = res.Bid.Bid
      this.props.auction.highestBidder = res.Bid
      await this.setState({overbid: true})
    }

    if (res && !res.success && res.errors) {
      await this.form.errors.set(res.errors)
      await this.setState({form: this.form})
    }

    if (res && res.success && this.props.auction.round.Type === 2) {
      this.props.auction.round.EndsOn = moment().format()
    }

    if (res && res.success) {
      // Create new objects to trigger React re-render (don't mutate)
      const updatedRound = {...this.props.auction.round, bid: res.Bid}
      const updatedAuction = {
        ...this.props.auction,
        highestBid: res.Bid.Bid,
        highestBidder: res.Bid,
        round: updatedRound,
        // Use backend calculation - user might still be outbid if their bid wasn't high enough
        isOutbid: res.isStillOutbid || false
      }

      await store.dispatch(showAuction(updatedAuction))
      
      // Also update the auctions list
      const updatedAuctions = this.auctions.auctions.map(a => 
        a.Id === updatedAuction.Id ? updatedAuction : a
      )
      await store.dispatch(setAuctions(updatedAuctions))
      
      this.props.hideModal && this.props.hideModal()
    }
  }

  changeBid = async (event: any) => {
    if (event && event.currentTarget) {
      const bid = _.toInteger(_.toString(event.currentTarget.value).replace('€', '').replace('.', ''))
      await this.setState({bid: bid})
    }
  }

  handleBidClick = () => {
    const {auction} = this.props
    if (
      auction.round.Type === 0 &&
      auction.round.bid &&
      this.state.bid < auction.round.bid.Bid
    ) {
      this.setState({correctionPending: true})
    } else {
      this.bid()
    }
  }

  cancelCorrection = () => {
    this.setState({correctionPending: false})
  }

  render() {
    const {auction} = this.props
    const {round} = auction
    const header = (
      <div className="px-3 py-2 text-gray-700 font-semibold">
        <span><Icon icon="gavel"/> <Trans>{auction.round.Type !== 2 ? 'Bieten' : 'Kaufen'}</Trans></span>
      </div>
    )
    const footer = (
      <div className="flex flex-col md:flex-row justify-between p-2">
        {
          round.Type === 1 || round.Type === 0 ?
            <div className="flex flex-row flex-wrap mb-2 md:mb-0">
              {
                !this.state.correctionPending && _.map(this.state.steps, step => (
                  <div key={JSON.stringify(step)}>
                    <button className="border-0 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 mr-2 font-semibold shadow-md" type="button" onClick={() => this.setState({bid: _.toInteger(this.state.bid) + _.toInteger(step.step)})}> + {currency(step.step)}</button>
                  </div>
                ))
              }
            </div>
            : <div/>
        }
        <div className="flex flex-row justify-end">
          <div>
            <button disabled={this.state.form.busy} onClick={() => this.props.hideModal()} className={"border-0 rounded text-gray-500 hover:bg-gray-200 px-2 py-1 mr-4 " + (this.state.form.busy ? 'cursor-not-allowed opacity-75' : '')} type="button">
              <Trans>Abbrechen</Trans>
            </button>
          </div>

          {
            auction.round.Type !== 2 ?
              <div className="flex flex-row">
                {this.state.correctionPending &&
                  <button
                    disabled={this.state.form.busy}
                    onClick={this.cancelCorrection}
                    className={"border-0 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 mr-2 font-semibold " + (this.state.form.busy ? 'cursor-not-allowed opacity-75' : '')}
                    type="button">
                    <Trans>Betrag ändern</Trans>
                  </button>
                }
                <button disabled={this.state.form.busy || (auction.round.Type !== 0 && !this.props.auction.round.PriceCall && _.toInteger(_.replace(_.toString(this.state.bid), '€', '')) <= _.toInteger(this.props.auction.highestBid))} key={this.state.form.busy ? 1 : 0} onClick={this.state.correctionPending ? this.bid : this.handleBidClick} className={
                  "border-0 rounded bg-gray-300 hover:bg-gray-400 text-gray-700 px-2 py-1 shadow font-semibold "
                  + (this.state.form.busy || (auction.round.Type !== 0 && !this.props.auction.round.PriceCall && _.toInteger(_.replace(_.toString(this.state.bid), '€', '')) <= _.toInteger(this.props.auction.highestBid))
                    ? 'cursor-not-allowed opacity-75' : '')
                  + (round.Type === 0 ? ' bg-orange-500 text-orange-100 hover:bg-orange-600' : '')
                  + (round.Type === 1 ? ' bg-blue-500 text-blue-100 hover:bg-blue-600' : '')
                  + (round.Type === 2 ? ' bg-purple-500 text-purple-100 hover:bg-purple-600' : '')
                } type="button">
                  <div>
                    {
                      this.state.form.busy ?
                        <Icon icon="spinner" className="fa-spin"/>
                        :
                        <Icon icon={this.state.correctionPending ? 'check' : 'gavel'}/>
                    }
                    <span> {this.state.correctionPending ? <Trans>Ja, korrigieren</Trans> : <Trans>Bieten</Trans>}</span>
                  </div>
                </button>
              </div>
              : round.FixedPrice ?
                <button disabled={this.state.form.busy} onClick={this.bid} className={
                  "border-0 rounded bg-gray-300 hover:bg-gray-400 text-gray-700 px-2 py-1 shadow font-semibold "
                  + (this.state.form.busy ? 'cursor-not-allowed opacity-75' : '')
                  + (round.Type === 0 ? ' bg-orange-500 text-orange-100 hover:bg-orange-600' : '')
                  + (round.Type === 1 ? ' bg-blue-500 text-blue-100 hover:bg-blue-600' : '')
                  + (round.Type === 2 ? ' bg-purple-500 text-purple-100 hover:bg-purple-600' : '')
                } type="button">
                  {
                    this.state.form.busy ?
                      <Icon icon="spinner" className="fa-spin"/>
                      :
                      <Icon icon="check"/>
                  }

                  <span> <Trans>Sofort Kauf</Trans></span>
                </button>
                : null
          }
        </div>
      </div>
    )

    const body = (
      <div className={"text-gray-700 flex flex-col"}>
        <Countdown auction={auction} timeTillDate={auction.round.EndsOn}/>
        <div className="p-2 flex flex-col">

          <div className="font-semibold leading-relaxed text-gray-600 text-sm mt-2">
            {make(this.state.subject.Make)} {model(this.state.subject.Make, this.state.subject.Model)}
          </div>

          <div>
            {
              auction.round.Type === 0 ?
                <span className="text-gray-600 mt-2">
                  {
                    round.MinPrice ?
                      <div>
                        <span className="mr-2">{round.bid ? currency(round.bid.Bid) : currency(round.MinPrice)}</span>
                        <span className='text-xs font-normal'><Trans>{round.bid ? 'Ihr Gebot' : 'Mindestpreis'}</Trans></span>
                      </div>
                      :
                      round.PriceCall ?
                        <div>
                          <span className="mr-2">{round.bid ? currency(round.bid.Bid) : currency(round.PriceCall)}</span>
                          <span className='text-xs font-normal'><Trans>{round.bid ? 'Ihr Gebot' : 'Orientierungspreis'}</Trans></span>
                        </div>
                        :
                        <div>
                          <span className="mr-2">{round.bid ? currency(round.bid.Bid) : <Trans>Kein Preis abgegeben</Trans>}</span>
                          <span className='text-xs font-normal'>{round.bid ? <Trans>Ihr Gebot</Trans> : null}</span>
                        </div>
                  }

                  {
                    round.UseNetPrices && hasFlag(_.get(auction.subjects, '0.vehicle', {}), 'SellFlags', 32768) ?
                      <span className='text-xs font-bold ml-1'><Trans>netto</Trans></span>
                      : null
                  }
                </span>
                : auction.round.Type === 1 ?
                  <span className="text-gray-600 mt-2">
                    {
                      round.MinPrice ?
                        <div>
                          <span className="mr-1">{round.bid ? currency(round.bid.Bid) : currency(round.MinPrice)}</span>
                          <span className='text-xs font-normal'><Trans>{round.bid ? 'Ihr Gebot' : 'Mindestpreis'}</Trans></span>
                        </div>
                        : round.PriceCall ?
                          <div>
                            <span className="mr-1">{round.bid ? currency(round.bid.Bid) : currency(round.PriceCall)}</span>
                            <span className='text-xs font-normal'><Trans>{round.bid ? 'Ihr Gebot' : 'Orientierungspreis'}</Trans></span>
                          </div>
                          :
                          <div>
                            <span className="mr-1">{this.props.auction.highestBid ? currency(this.props.auction.highestBid) : <Trans>Keine Gebote</Trans>}</span>
                            <span className='text-xs font-normal'>{this.props.auction.highestBid ? <Trans>Höchstgebot</Trans> : null}</span>
                          </div>
                    }

                    {
                      round.UseNetPrices && hasFlag(_.get(auction.subjects, '0.vehicle', {}), 'SellFlags', 32768) ?
                        <span className='text-xs font-bold ml-1'><Trans>netto</Trans></span>
                        : null
                    }
                  </span>
                  : auction.round.Type === 2 && round.FixedPrice ?
                    <span className="text-gray-600 mt-2">
                      <div>
                        <span className="mr-1">
                          <Trans>Zum Sofort Kauf verfügbar für</Trans> {currency(round.FixedPrice)}
                          {
                            round.UseNetPrices && hasFlag(_.get(auction.subjects, '0.vehicle', {}), 'SellFlags', 32768) ?
                              <span className='text-xs font-bold ml-1'><Trans>netto</Trans></span>
                              : null
                          }
                        </span>
                        <span className='text-xs font-normal'/>
                      </div>
                    </span>
                    : null
            }
          </div>

          {this.state.overbid && this.props.auction.highestBid ?
            <AlertWarning> <Trans>Ihr Gebot war nicht erfolgreich. Bitte bieten Sie mehr als das neue Höchstgebot von</Trans> {currency(this.props.auction.highestBid)}.</AlertWarning>
            : null}

          {
            this.state.form.errors.has('auction') ?
              <AlertDanger> <Trans>{auction.round.Type === 2 ? 'Das Angebot wurde bereits verkauft.' : 'Die Auktion wurde bereits beendet.'}</Trans></AlertDanger>
              : null
          }

          {
            this.state.form.errors.has('StartsOn') ?
              <AlertDanger><Trans>Die Auktion wurde noch nicht gestartet.</Trans></AlertDanger>
              : null
          }

          {this.state.form.errors.has('bid') ?
            <AlertDanger><Trans>{this.state.form.errors.get('bid') || ''}</Trans></AlertDanger>
            : null}

          {this.state.correctionPending && auction.round.bid ?
            <AlertWarning><Trans>Ihr aktuelles Gebot beträgt</Trans> <strong>{currency(auction.round.bid.Bid)}</strong>. <Trans>Soll es auf</Trans> <strong>{currency(this.state.bid)}</strong> <Trans>korrigiert werden?</Trans></AlertWarning>
            : null}

          {
            auction.round.Type !== 2 && !this.state.correctionPending ?
              <CurrencyInput placeholder="0.00€" type="text" value={this.state.bid} onChange={this.changeBid} className="shadow appearance-none block w-full bg-gray-200 text-gray-800 border border-gray-200 rounded py-2 px-3 leading-tight hover:bg-gray-200 focus:bg-gray-200 focus:border-gray-300 text-center font-semibold text-4xl"/>
              : null
          }
        </div>
      </div>
    )
    return (
      <Modal header={header} footer={footer} body={body} maxWidth="lg"/>
    )
  }
}

export default connect((state: GlobalState) => ({
  app: state.app,
  auctionsState: state.auctions,
  bid: !state.auctions.auction ? 0 : state.auctions.auction.round.Type === 2 ? _.toInteger(state.auctions.auction.round.FixedPrice) : (_.toInteger(state.auctions.auction.round.PriceCall) || _.toInteger(state.auctions.auction.highestBid) || 0)
}))(BidModal)
